import React, { useCallback, useEffect, useState, useRef } from "react";
import styles from "./AppRegistration.module.css";
import "../../../styles/components.css";
import { fetchAuthSession } from "aws-amplify/auth";
import { FaSpinner } from "react-icons/fa";

const APPLE_TEAM_ID_PATTERN = /^[A-Z0-9]{10}$/;
const APPLE_BUNDLE_ID_PATTERN = /^(?=.{1,255}$)[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*$/;
const SWIFT_OAUTH_GUIDE =
  "https://smartspectra.presagetech.com/docs/swift/option-2-oauth/";

const registrationIdentity = (entry) => entry.id || entry.registration_id;
const isApplePlatform = (platform) => platform?.toLowerCase() === "apple";
const isSwappedAppleRegistration = (entry) =>
  Boolean(registrationIdentity(entry)) &&
  isApplePlatform(entry.platform) &&
  APPLE_TEAM_ID_PATTERN.test(entry.app_id || "") &&
  APPLE_BUNDLE_ID_PATTERN.test(entry.org_id || "") &&
  // A stored org_id that normalizes to a valid Team ID (e.g. lowercase) means
  // the row is plausibly correct, so no correction is offered.
  !APPLE_TEAM_ID_PATTERN.test((entry.org_id || "").trim().toUpperCase());
const authenticatedToken = async () => {
  const session = await fetchAuthSession();
  return session?.tokens?.accessToken;
};
const sandboxHelp = (entry) => {
  if (isApplePlatform(entry.platform)) {
    return entry.sandbox
      ? "Development App Attest identities are accepted in addition to production identities. Production, TestFlight, and App Store builds continue to work."
      : "Only production App Attest identities are accepted. Development identities are rejected.";
  }
  return entry.sandbox
    ? "Play Integrity licensing and app-recognition checks are relaxed. Device integrity, certificate matching, and freshness remain enforced."
    : "Production licensing and app recognition are required. Device integrity, certificate matching, and freshness remain enforced.";
};

function AppRegistration() {
  const [platform, setPlatform] = useState("Apple");
  const [appIdInput, setAppIdInput] = useState("");
  const [orgIdInput, setOrgIdInput] = useState("");
  const [certificateShaInput, setCertificateShaInput] = useState("");
  // DevRM fields
  const [appShortDescription, setAppShortDescription] = useState("");
  const [applicationType, setApplicationType] = useState("");
  const [usageDomain, setUsageDomain] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [registrationsError, setRegistrationsError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [actionError, setActionError] = useState("");
  const [pendingAction, setPendingAction] = useState("");

  const didFetchRegistrations = useRef(false);

  // Android package names: letters, digits, underscores, periods (e.g., com.example.my_app)
  const sanitizeAndroidAppId = (val) => val.trim().replace(/[^0-9a-zA-Z._-]/g, "");
  const sanitizeSha = (val) => val.trim().replace(/[^0-9a-fA-F:]/g, ""); // SHA256 fingerprint: hex digits and colons

  const validateAppleIdentifiers = (bundleId, teamId) => {
    const errors = {};
    const normalizedBundleId = bundleId.trim();
    const normalizedTeamId = teamId.trim().toUpperCase();
    const swapped =
      APPLE_TEAM_ID_PATTERN.test(normalizedBundleId) &&
      APPLE_BUNDLE_ID_PATTERN.test(normalizedTeamId) &&
      !APPLE_TEAM_ID_PATTERN.test(normalizedTeamId);

    if (swapped) {
      errors.identifiers = "The Bundle ID and Apple Team ID appear to be swapped.";
    }
    if (!APPLE_BUNDLE_ID_PATTERN.test(normalizedBundleId)) {
      errors.appId =
        "Use letters, numbers, and hyphens in non-empty segments separated by optional dots (255 characters maximum).";
    }
    if (!APPLE_TEAM_ID_PATTERN.test(normalizedTeamId)) {
      errors.orgId = "Apple Team ID must be exactly 10 uppercase letters or numbers.";
    }
    return errors;
  };

  const triggerDownload = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };

  async function deleteAppId(entry) {
    const entryId = registrationIdentity(entry);
    const confirmed = window.confirm(
      `Delete ${entry.app_id} (${entry.platform})? This action cannot be undone.`
    );
    if (!confirmed) return;
    const actionKey = `delete:${entryId}`;
    setPendingAction(actionKey);
    setActionError("");
    try {
      const jwt = await authenticatedToken();

      if (!jwt) {
        setActionError("Your session expired. Please sign in again.");
        return;
      }

      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;
      const res = await fetch(url, {
        method: "DELETE",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: entryId }),
      });

      const json = res.ok ? await res.json() : null;
      if (res.ok && json?.response?.resultStatus === "SUCCESS") {
        await fetchRegistrations();
      } else {
        setActionError("The application could not be deleted. Please try again.");
      }
    } catch {
      setActionError("The application could not be deleted. Please try again.");
    } finally {
      setPendingAction("");
    }
  }

  async function downloadConfig(entry) {
    const entryId = registrationIdentity(entry);
    const actionKey = `download:${entryId}`;
    setPendingAction(actionKey);
    setActionError("");
    try {
      const jwt = await authenticatedToken();

      if (!jwt) {
        setActionError("Your session expired. Please sign in again.");
        return;
      }

      const persistedId = registrationIdentity(entry);
      const identifierQuery = persistedId
        ? `registration_id=${encodeURIComponent(persistedId)}`
        : `app_id=${encodeURIComponent(entry.app_id)}`;
      const configPlatform = isApplePlatform(entry.platform) ? "Apple" : "Android";
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_CONFIG}` +
        `?platform=${configPlatform}&${identifierQuery}`;

      const res = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (!res.ok) throw new Error("config-download-failed");
      const blob = await res.blob();
      triggerDownload(
        blob,
        isApplePlatform(entry.platform) ? "PresageService-Info.plist" : "presage_services.xml"
      );
    } catch {
      setActionError("We could not download the configuration file. Please try again.");
    } finally {
      setPendingAction("");
    }
  }

  const fetchRegistrations = useCallback(async () => {
    setLoadingRegistrations(true);
    setRegistrationsError("");
    try {
      const jwt = await authenticatedToken();

      if (!jwt) {
        setRegistrations([]);
        setRegistrationsError("Your session expired. Please sign in again.");
        return null;
      }

      // 2. Call your status endpoint
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_VERIFICATION_STATUS}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!res.ok) {
        setRegistrations([]);
        setRegistrationsError("We could not load your registered applications.");
        return null;
      }

      const json = await res.json();
      const { resultStatus, resultMessage } = json.response || {};

      let regs;
      if (resultStatus === "SUCCESS" && Array.isArray(resultMessage?.app_ids)) {
        regs = resultMessage.app_ids;
      } else if (resultStatus === "SUCCESS" && resultMessage === "No registered app_ids") {
        regs = [];
      } else {
        setRegistrations([]);
        setRegistrationsError("We could not load your registered applications.");
        return null;
      }

      // 4. Update state: always set the array, and show form only if empty
      setRegistrations(regs);
      setShowRegisterForm(regs.length === 0);
      return regs;
    } catch {
      setRegistrations([]);
      setRegistrationsError("We could not load your registered applications.");
      return null;
    } finally {
      setLoadingRegistrations(false);
    }
  }, []);

  useEffect(() => {
    if (didFetchRegistrations.current) return;
    didFetchRegistrations.current = true;
    fetchRegistrations();
  }, [fetchRegistrations]);

  function handlePlatformChange(e) {
    setPlatform(e.target.value);
    // clear the other field if you like:
    setAppIdInput("");
    setOrgIdInput("");
    setCertificateShaInput("");
    setAppShortDescription("");
    setApplicationType("");
    setUsageDomain("");
    setFormErrors({});
    setFormError("");
  }

  async function registerAppId() {
    const normalizedAppId =
      platform === "Apple" ? appIdInput.trim() : sanitizeAndroidAppId(appIdInput);
    const normalizedOrgId = orgIdInput.trim().toUpperCase();
    if (platform === "Apple") {
      const errors = validateAppleIdentifiers(normalizedAppId, normalizedOrgId);
      setFormErrors(errors);
      setAppIdInput(normalizedAppId);
      setOrgIdInput(normalizedOrgId);
      if (Object.keys(errors).length > 0) return;
    }
    setRegisterLoading(true);
    setFormError("");
    try {
      const jwt = await authenticatedToken();

      if (!jwt) {
        setFormError("Your session expired. Please sign in again.");
        return;
      }

      const headers = {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      };

      const body = {
        platform,
        app_id: normalizedAppId,
        // include exactly one of the two:
        ...(platform === "Apple"
          ? { org_id: normalizedOrgId }
          : { certificate_sha: certificateShaInput }),
        // DevRM fields
        app_short_description: appShortDescription,
        application_type: applicationType,
        usage_domain: usageDomain,
      };

      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      
      if (!res.ok) {
        setFormError("The application could not be registered. Check the fields and try again.");
        return;
      }
      
      const data = await res.json();

      if (data?.response?.resultStatus === "SUCCESS") {
        setShowRegisterForm(false);
        // Reset form
        setAppIdInput("");
        setOrgIdInput("");
        setCertificateShaInput("");
        setAppShortDescription("");
        setApplicationType("");
        setUsageDomain("");
        fetchRegistrations();
      } else {
        setFormError("The application could not be registered. Check the fields and try again.");
      }
    } catch {
      setFormError("The application could not be registered. Please try again.");
    } finally {
      setRegisterLoading(false);
    }
  }

  async function correctSwappedIdentifiers(entry) {
    const entryId = registrationIdentity(entry);
    const correctedBundleId = entry.org_id.trim();
    const correctedTeamId = entry.app_id.trim().toUpperCase();
    const confirmed = window.confirm(
      `Correct the saved Apple identifiers for ${entry.app_id}?\n\n` +
        `Bundle ID: ${entry.app_id} → ${correctedBundleId}\n` +
        `Apple Team ID: ${entry.org_id} → ${correctedTeamId}\n\n` +
        `The persisted registration will be updated in place.`
    );
    if (!confirmed) return;
    const actionKey = `correct:${entryId}`;
    setPendingAction(actionKey);
    setActionError("");
    try {
      const jwt = await authenticatedToken();
      if (!jwt) {
        setActionError("Your session expired. Please sign in again.");
        return;
      }
      const res = await fetch(
        `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`,
        {
          method: "POST",
          mode: "cors",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: entryId,
            platform: entry.platform,
            app_id: correctedBundleId,
            org_id: correctedTeamId,
            sandbox: entry.sandbox === true,
            app_short_description: entry.app_short_description || "",
            application_type: entry.application_type || "",
            usage_domain: entry.usage_domain || "",
          }),
        }
      );
      const data = res.ok ? await res.json() : null;
      if (!res.ok || data?.response?.resultStatus !== "SUCCESS") {
        setActionError("The saved identifiers could not be corrected. Please try again.");
        return;
      }
      // A backend without in-place correction reports SUCCESS but appends a
      // duplicate registration, leaving the original row swapped. Verify the
      // persisted row actually changed and fail loudly if it did not.
      const refreshed = await fetchRegistrations();
      const persisted = refreshed?.find((r) => registrationIdentity(r) === entryId);
      if (persisted && isSwappedAppleRegistration(persisted)) {
        setActionError(
          "The saved identifiers were not corrected in place. Review your registered applications and remove any duplicate before retrying."
        );
      }
    } catch {
      setActionError("The saved identifiers could not be corrected. Please try again.");
    } finally {
      setPendingAction("");
    }
  }

  async function handleEnableSandbox(entry) {
    const enabling = entry.sandbox === true;
    let confirmation;
    if (isApplePlatform(entry.platform)) {
      confirmation = enabling
        ? `Enable sandbox for ${entry.app_id} (${entry.platform})? Development App Attest identities will be accepted in addition to production identities; production, TestFlight, and App Store builds will continue to work.`
        : `Disable sandbox for ${entry.app_id} (${entry.platform})? Only production App Attest identities will be accepted; development identities will be rejected.`;
    } else {
      confirmation = enabling
        ? `Enable sandbox for ${entry.app_id} (${entry.platform})? This relaxes Play Integrity licensing and app-recognition checks; device integrity, certificate matching, and freshness remain enforced.`
        : `Disable sandbox for ${entry.app_id} (${entry.platform})? Production Play Integrity licensing and app recognition will be required; device integrity, certificate matching, and freshness remain enforced.`;
    }
    const confirmed = window.confirm(confirmation);
    if (!confirmed) return;

    const entryId = registrationIdentity(entry);
    const actionKey = `sandbox:${entryId}`;
    setPendingAction(actionKey);
    setActionError("");
    try {
      const jwt = await authenticatedToken();

      if (!jwt) {
        setActionError("Your session expired. Please sign in again.");
        return;
      }

      // Confirmed usage of the consistent endpoint:
      const url = `${process.env.REACT_APP_USAGE_API}${process.env.REACT_APP_APP_ID}`;
      // Build the body object with required fields depending on platform
      const body = JSON.stringify({
        id: entryId,
        platform: entry.platform,
        app_id: entry.app_id,
        ...(isApplePlatform(entry.platform) && entry.org_id
          ? { org_id: entry.org_id }
          : !isApplePlatform(entry.platform) && entry.certificate_sha
          ? { certificate_sha: entry.certificate_sha }
          : {}),
        sandbox: entry.sandbox,
        app_short_description: entry.app_short_description || "",
        application_type: entry.application_type || "",
        usage_domain: entry.usage_domain || "",
      });
      const res = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body,
      });

      const json = await res.json();
      if (res.ok && json?.response?.resultStatus === "SUCCESS") {
        await fetchRegistrations();
      } else {
        setActionError("Sandbox mode could not be updated. Please try again.");
      }
    } catch {
      setActionError("Sandbox mode could not be updated. Please try again.");
    } finally {
      setPendingAction("");
    }
  }

  return (
    <div className={styles["app-registration-container"]}>
      <div className={styles["app-registration-content"]}>
        <div className={styles["page-header"]}>
          <h1>App Registration for OAuth</h1>
          <p>Register your applications to enable OAuth authentication with the Presage API</p>
        </div>

        <div className={styles["registration-section"]}>
          {loadingRegistrations ? (
            // Loading state
            <div className={styles["loading-container"]}>
              <FaSpinner className={styles["spinner"]} />
              <p>Loading your registered applications...</p>
            </div>
          ) : registrationsError ? (
            <div className={styles["error-state"]} role="alert">
              <p>{registrationsError}</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchRegistrations}
              >
                Retry
              </button>
            </div>
          ) : registrations.length === 0 || showRegisterForm ? (
            // No registrations or showing form
            <div className={styles["register-form-container"]}>
              <div className={styles["form-header"]}>
                <h2>Register New Application</h2>
              </div>
              <p className={styles["subtitle"]} style={{ fontSize: "14px", marginBottom: "20px" }}>
                Learn how to register your app for OAuth{" "}
                <a
                  href={SWIFT_OAUTH_GUIDE}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#c91e1e",
                    textDecoration: "underline",
                  }}
                >
                  in the Swift OAuth setup guide
                </a>
              </p>

              {/* Platform Selection */}
              <div className={styles["platform-selection"]}>
                <h3>Platform</h3>
                <div className={styles["radio-group"]}>
                  <label className={styles["radio-label"]}>
                    <input
                      type="radio"
                      name="platform"
                      value="Apple"
                      checked={platform === "Apple"}
                      onChange={handlePlatformChange}
                    />
                    <span className={styles["radio-custom"]}></span>
                    Apple iOS
                  </label>
                  <label className={styles["radio-label"]}>
                    <input
                      type="radio"
                      name="platform"
                      value="Android"
                      checked={platform === "Android"}
                      onChange={handlePlatformChange}
                    />
                    <span className={styles["radio-custom"]}></span>
                    Android
                  </label>
                </div>
              </div>

              {/* App ID Field */}
              <div className={styles["field-group"]}>
                <label className={styles["field-label"]} htmlFor="app-registration-app-id">
                  {platform === "Apple" ? "Bundle ID" : "Application ID"}{" "}
                  <span className={styles["required-indicator"]}>*</span>
                </label>
                <input
                  id="app-registration-app-id"
                  required
                  className={`${styles["input-field"]} ${formErrors.appId ? styles["input-error"] : ""}`}
                  type="text"
                  placeholder="com.example.app"
                  value={appIdInput}
                  maxLength={platform === "Apple" ? 255 : undefined}
                  aria-invalid={Boolean(formErrors.appId || formErrors.identifiers)}
                  aria-describedby="app-id-help app-id-error apple-identifier-error"
                  onChange={(e) => {
                    setAppIdInput(e.target.value);
                    setFormErrors({});
                  }}
                  onBlur={() => {
                    const normalized =
                      platform === "Apple" ? appIdInput.trim() : sanitizeAndroidAppId(appIdInput);
                    setAppIdInput(normalized);
                    if (platform === "Apple") {
                      setFormErrors(validateAppleIdentifiers(normalized, orgIdInput));
                    }
                  }}
                />
                <div id="app-id-help" className={styles["field-help"]}>
                  {platform === "Apple" && (
                    <span>
                      Enter the Bundle ID exactly as it appears in Xcode. Dots are optional;
                      each segment may contain letters, numbers, and hyphens.{" "}
                      <a
                        href="https://developer.apple.com/documentation/appstoreconnectapi/bundle-ids"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </span>
                  )}
                  {platform === "Android" && (
                    <span>
                      Your Application ID (package name).{" "}
                      <a
                        href="https://developer.android.com/studio/build/application-id"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </a>
                    </span>
                  )}
                </div>
                {formErrors.appId && (
                  <p id="app-id-error" className={styles["field-error"]}>
                    {formErrors.appId}
                  </p>
                )}
              </div>

              {/* Platform-specific fields */}
              {platform === "Apple" ? (
                <div className={styles["field-group"]}>
                  <label className={styles["field-label"]} htmlFor="app-registration-team-id">
                    Apple Team ID <span className={styles["required-indicator"]}>*</span>
                  </label>
                  <input
                    id="app-registration-team-id"
                    required
                    className={`${styles["input-field"]} ${formErrors.orgId ? styles["input-error"] : ""}`}
                    type="text"
                    placeholder="ABC123DEF4"
                    value={orgIdInput}
                    autoCapitalize="characters"
                    aria-invalid={Boolean(formErrors.orgId || formErrors.identifiers)}
                    aria-describedby="team-id-help team-id-error apple-identifier-error"
                    onChange={(e) => {
                      setOrgIdInput(e.target.value);
                      setFormErrors({});
                    }}
                    onBlur={() => {
                      const normalized = orgIdInput.trim().toUpperCase();
                      setOrgIdInput(normalized);
                      setFormErrors(validateAppleIdentifiers(appIdInput, normalized));
                    }}
                  />
                  <div id="team-id-help" className={styles["field-help"]}>
                    The 10-character Team ID from your Apple Developer account.{" "}
                    <a
                      href="https://developer.apple.com/help/account/manage-your-team/locate-your-team-id/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </div>
                  {formErrors.orgId && (
                    <p id="team-id-error" className={styles["field-error"]}>
                      {formErrors.orgId}
                    </p>
                  )}
                </div>
              ) : (
                <div className={styles["field-group"]}>
                  <label className={styles["field-label"]}>
                    Certificate SHA256 Fingerprint <span className={styles["required-indicator"]}>*</span>
                  </label>
                  <input
                    required
                    className={styles["input-field"]}
                    type="text"
                    placeholder="Certificate SHA256 Fingerprint"
                    value={certificateShaInput}
                    onChange={(e) => setCertificateShaInput(e.target.value)}
                    onBlur={() =>
                      setCertificateShaInput(sanitizeSha(certificateShaInput))
                    }
                  />
                  <div className={styles["field-help"]}>
                    Your Android signing certificate SHA-256 fingerprint.{" "}
                    <a
                      href="https://developers.google.com/android/guides/client-auth"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </div>
                </div>
              )}

              {platform === "Apple" && formErrors.identifiers && (
                <p id="apple-identifier-error" className={styles["form-error"]} role="alert">
                  {formErrors.identifiers}
                </p>
              )}
              {platform === "Apple" && appIdInput.trim() && orgIdInput.trim() && (
                <p className={styles["identifier-preview"]}>
                  App Attest identity preview: <code>{orgIdInput.trim().toUpperCase()}.{appIdInput.trim()}</code>
                </p>
              )}

              {/* DevRM fields */}
              <div className={styles["devRM-section"]}>
                <h3>Application Details</h3>
                <div className={styles["devRM-fields"]}>
                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      App Description <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <input
                      required
                      className={styles["input-field"]}
                      type="text"
                      placeholder="Brief description of your app"
                      value={appShortDescription}
                      onChange={(e) => setAppShortDescription(e.target.value)}
                    />
                  </div>

                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      Application Type <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <select
                      required
                      className={styles["input-field"]}
                      value={applicationType}
                      onChange={(e) => setApplicationType(e.target.value)}
                    >
                      <option value="">Select application type</option>
                      <option value="Monitoring">Monitoring</option>
                      <option value="Therapy">Therapy</option>
                      <option value="Diagnosis">Diagnosis</option>
                      <option value="Treatment">Treatment</option>
                      <option value="Security">Security</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className={styles["field-group"]}>
                    <label className={styles["field-label"]}>
                      Usage Domain <span className={styles["required-indicator"]}>*</span>
                    </label>
                    <select
                      required
                      className={styles["input-field"]}
                      value={usageDomain}
                      onChange={(e) => setUsageDomain(e.target.value)}
                    >
                      <option value="">Select usage domain</option>
                      <option value="Outpatient">Outpatient</option>
                      <option value="Inpatient">Inpatient</option>
                      <option value="Gaming">Gaming</option>
                      <option value="Sports/Performance">Sports/Performance</option>
                      <option value="Wellness">Wellness</option>
                      <option value="Space">Space</option>
                      <option value="Military">Military</option>
                      <option value="Intel">Intel</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles["form-actions"]}>
                {formError && (
                  <p className={styles["form-error"]} role="alert">
                    {formError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={registerAppId}
                  disabled={
                    registerLoading ||
                    !appIdInput ||
                    !appShortDescription ||
                    !applicationType ||
                    !usageDomain ||
                    (platform === "Apple" ? !orgIdInput : !certificateShaInput) ||
                    (platform === "Apple" &&
                      Object.keys(validateAppleIdentifiers(appIdInput, orgIdInput)).length > 0)
                  }
                  className={`btn btn-primary ${styles["register-btn"]}`}
                >
                  {registerLoading ? (
                    <>
                      <FaSpinner className={styles["spinner"]} />
                      Registering...
                    </>
                  ) : (
                    "Register Application"
                  )}
                </button>
                {registrations.length !== 0 && showRegisterForm && (
                  <button
                    type="button"
                    onClick={() => setShowRegisterForm(false)}
                    className={`btn btn-secondary ${styles["cancel-btn"]}`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Has registrations and not showing form
            <div className={styles["registrations-list"]}>
              <div className={styles["list-header"]}>
                <h2>Your Registered Applications</h2>
              </div>
              <p className={styles["subtitle"]} style={{ fontSize: "14px", marginBottom: "20px" }}>
                Learn how to use OAuth in your app{" "}
                <a
                  href={SWIFT_OAUTH_GUIDE}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#c91e1e",
                    textDecoration: "underline",
                  }}
                >
                  in the Swift OAuth setup guide
                </a>
              </p>

              {actionError && (
                <p className={styles["form-error"]} role="alert">
                  {actionError}
                </p>
              )}

              <div className={styles["apps-table-container"]}>
                <table className={styles["apps-table"]}>
                  <thead>
                    <tr>
                      <th>Bundle ID / Application ID</th>
                      <th>Platform</th>
                      <th>Sandbox Status</th>
                      <th>Download Config</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((r) => {
                      const identity = registrationIdentity(r);
                      const sandboxPending = pendingAction === `sandbox:${identity}`;
                      const downloadPending = pendingAction === `download:${identity}`;
                      const deletePending = pendingAction === `delete:${identity}`;
                      const correctionPending = pendingAction === `correct:${identity}`;
                      return (
                      <tr key={identity || `${r.platform}:${r.app_id}`}>
                        <td>
                          {r.app_id}
                          {isSwappedAppleRegistration(r) && (
                            <div className={styles["correction-callout"]}>
                              <span>These saved Apple identifiers appear to be swapped.</span>
                              <button
                                type="button"
                                onClick={() => correctSwappedIdentifiers(r)}
                                disabled={Boolean(pendingAction)}
                                className={`btn btn-secondary btn-sm ${styles["correction-btn"]}`}
                              >
                                {correctionPending ? "Correcting..." : "Correct swapped IDs"}
                              </button>
                            </div>
                          )}
                        </td>
                        <td>{r.platform}</td>
                        <td>
                          <div className={styles["sandbox-cell"]}>
                            <span className={r.sandbox ? styles["enabled"] : styles["disabled"]}>
                              {r.sandbox ? "Enabled" : "Disabled"}
                            </span>
                            <span className={styles["sandbox-help"]}>
                              {sandboxHelp(r)}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleEnableSandbox({ ...r, sandbox: !r.sandbox })}
                              disabled={Boolean(pendingAction)}
                              aria-label={`${r.sandbox ? "Disable" : "Enable"} sandbox for ${r.app_id}`}
                              className={`btn btn-secondary btn-sm ${styles["toggle-btn"]}`}
                            >
                              {sandboxPending ? "Updating..." : r.sandbox ? "Disable" : "Enable"}
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className={styles["download-actions"]}>
                            {r.org_id && (
                              <button
                                type="button"
                                onClick={() => downloadConfig(r)}
                                disabled={Boolean(pendingAction)}
                                className={`btn btn-secondary btn-sm ${styles["download-btn"]}`}
                              >
                                {downloadPending ? "Downloading..." : "Download .plist"}
                              </button>
                            )}
                            {r.certificate_sha && (
                              <button
                                type="button"
                                onClick={() => downloadConfig(r)}
                                disabled={Boolean(pendingAction)}
                                className={`btn btn-secondary btn-sm ${styles["download-btn"]}`}
                              >
                                {downloadPending ? "Downloading..." : "Download .xml"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => deleteAppId(r)}
                            disabled={Boolean(pendingAction)}
                            aria-label={`Delete ${r.app_id} (${r.platform})`}
                            className={`btn btn-danger btn-sm ${styles["delete-btn"]}`}
                          >
                            {deletePending ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>

              <div className={styles["add-app-container"]}>
                <button
                  type="button"
                  onClick={() => setShowRegisterForm(true)}
                  className={`btn btn-primary ${styles["add-app-btn"]}`}
                >
                  Register Another Application
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppRegistration;
